import type { CategoriaDeStatus } from '@/features/tickets/types';

/**
 * O formato do que a API de workflow devolve.
 *
 * Declarado aqui, e não importado de `@central/workflow`, porque o que
 * atravessa a rede é JSON: `at` é string ISO e não `Instant`, `seq` é o texto
 * de um `BigInt` e não um número. Copiar o tipo do motor daria a impressão
 * errada de que os dois são a mesma coisa, e o erro só apareceria em execução.
 *
 * Nada aqui é inventado: cada campo existe em alguma resposta de
 * `apps/api/src/itsm/workflow/workflow.controller.ts`.
 */

// ── catálogo ───────────────────────────────────────────────────────────────

export type StatusDeVersao = 'DRAFT' | 'PUBLISHED' | 'RETIRED';

export const ROTULO_DE_VERSAO: Record<StatusDeVersao, string> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicada',
  RETIRED: 'Aposentada',
};

/**
 * O que a listagem traz de cada versão.
 *
 * Repare no que **não** vem: o grafo. `listDefinitions` seleciona quatro
 * campos, e não há rota que leia uma versão inteira — ver a nota em
 * `FluxoPage`, que é onde isso aparece para quem usa.
 */
export interface ResumoDeVersao {
  id: string;
  versionNumber: number;
  status: StatusDeVersao;
  publishedAt: string | null;
}

export interface DefinicaoDeFluxo {
  id: string;
  key: string;
  name: string;
  /** `ticket`, `project`, `article`… texto livre no servidor. */
  entityType: string;
  description: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
  versions: ResumoDeVersao[];
}

/** A linha inteira, como `POST …/draft` e `POST …/publish` a devolvem. */
export interface VersaoDeFluxo extends ResumoDeVersao {
  definitionId: string;
  graph: Record<string, unknown>;
  publishedById: string | null;
  createdAt: string;
  updatedAt: string;
  /** Carimbo de concorrência **da linha** — não é `versionNumber`. */
  version: number;
}

/**
 * Um problema encontrado no grafo, com o caminho dentro dele.
 *
 * `code` fica como `string` e não como união fechada de propósito: o motor
 * pode ganhar um código novo antes desta tela, e um `switch` exaustivo faria a
 * lista inteira sumir por causa de um valor desconhecido. O rótulo tem
 * fallback; a mensagem, que é o que importa, vem pronta do servidor.
 */
export interface Diagnostico {
  code: string;
  /** Onde, na definição: `transitions[2].guards[0]`. */
  path: string;
  message: string;
}

/** Nomes em português para os códigos que o motor emite hoje. */
export const ROTULO_DE_DIAGNOSTICO: Record<string, string> = {
  DUPLICATE_KEY: 'Chave repetida',
  INVALID_KEY: 'Chave inválida',
  UNKNOWN_STATE: 'Estado desconhecido',
  NO_INITIAL_STATE: 'Sem estado inicial',
  UNREACHABLE_STATE: 'Estado inalcançável',
  DEAD_END_STATE: 'Beco sem saída',
  TERMINAL_WITH_EXIT: 'Terminal com saída',
  AUTO_CYCLE: 'Ciclo automático',
  MISSING_EVENT: 'Evento ausente',
  UNKNOWN_EFFECT: 'Efeito fora do catálogo',
  BAD_EFFECT: 'Efeito mal formado',
  EMPTY_FROM: 'Origem vazia',
  MISSING_GUARD_MESSAGE: 'Guard sem mensagem',
  INVALID_EXPRESSION: 'Expressão inválida',
  EMPTY_DEFINITION: 'Definição vazia',
};

// ── transições de um chamado ───────────────────────────────────────────────

export interface EstadoDoFluxo {
  key: string;
  name: string;
  category: CategoriaDeStatus;
  terminal: boolean;
}

/**
 * Uma transição oferecida ao chamado — e o motivo, quando ela não vale.
 *
 * `enabled: false` com `blockedBecause` preenchido é **recusa**, não erro: o
 * servidor perguntou ao motor antes de a tela tentar, e devolveu a frase que o
 * guard (ou a permissão) escreveu. É por isso que o botão aparece apagado com
 * o motivo à vista, em vez de sumir.
 */
export interface OfertaDeTransicao {
  key: string;
  name: string;
  to: string;
  requiredPermission: string | null;
  requiredFields: string[];
  requiresReason: boolean;
  enabled: boolean;
  blockedBecause: string | null;
}

export interface TransicoesDoChamado {
  definitionKey: string;
  versionId: string;
  state: EstadoDoFluxo;
  transitions: OfertaDeTransicao[];
}

/** O corpo de `POST /tickets/:id/transitions`. */
export interface PedidoDeTransicao {
  key: string;
  fields?: Record<string, unknown>;
  reason?: string;
  /** A `version` do chamado que a tela leu: sem ela, a última escrita vence. */
  version?: number;
}

export interface PassoAplicado {
  transitionKey: string;
  fromState: string;
  toState: string;
  transitionLogId: string;
  /** Transição `AUTO` perseguida na mesma transação, sem ninguém apertar nada. */
  automatic: boolean;
}

// ── trilha ─────────────────────────────────────────────────────────────────

export type AcaoDeRelogio = 'PAUSE' | 'RESUME' | 'STOP' | 'UNCHANGED';

export const ROTULO_DE_RELOGIO: Record<AcaoDeRelogio, string> = {
  PAUSE: 'SLA pausado',
  RESUME: 'SLA retomado',
  STOP: 'SLA encerrado',
  UNCHANGED: 'SLA inalterado',
};

export type StatusDeEntrega = 'PENDING' | 'DELIVERED' | 'FAILED' | 'DEAD';

export const ROTULO_DE_ENTREGA: Record<StatusDeEntrega, string> = {
  PENDING: 'na fila',
  DELIVERED: 'aplicado',
  FAILED: 'falhou',
  DEAD: 'sem executor',
};

export interface EfeitoDeTransicao {
  transitionLogId: string;
  effectIndex: number;
  type: string;
  phase: 'SYNC' | 'ASYNC';
  origin: 'STATE_EXIT' | 'TRANSITION' | 'STATE_ENTRY';
  payload: Record<string, unknown>;
  status: StatusDeEntrega;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Uma passagem registrada — quem, quando, de onde para onde, e por quê.
 *
 * `seq` é `BigInt` no banco. Chega como número ou como texto conforme quem
 * serializa; a tela nunca faz conta com ele, só o usa como identidade estável.
 */
export interface RegistroDeTransicao {
  id: string;
  seq: number | string;
  workflowVersionId: string;
  entityType: string;
  entityId: string;
  transitionKey: string;
  fromState: string;
  toState: string;
  statusCategory: CategoriaDeStatus;
  slaClock: AcaoDeRelogio;
  terminal: boolean;
  at: string;
  actorId: string | null;
  reason: string | null;
  fields: Record<string, unknown>;
  correlationId: string;
  createdAt: string;
  effects: EfeitoDeTransicao[];
}
